import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, BodySmallBold } from 'styles/typography';

interface Props {
  // What the re-run will actually be sent with — read from the coverage, not from the last result, so
  // a payer or member ID corrected since the last run is what's shown.
  payerName: string;
  memberId: string;
  onConfirm: () => void;
  onCancel: () => void;
  isRerunning: boolean;
}

// Confirms a re-run before spending it (OLIO-2794). A verification costs a real payer call and replaces
// the result on screen, so the details it will run with are restated first — the common mistake is
// re-running the same wrong member ID and reading the same failure as a new one.
export default function RerunConfirmModal({ payerName, memberId, onConfirm, onCancel, isRerunning }: Props) {
  return (
    <StandardModal title='Re-Run Benefit Verification?' onCancel={onCancel} maxWidth='560px'>
      <Content>
        <BodySmallBold>
          Are you sure you wish to re-run a benefit verification with the details shown below?
        </BodySmallBold>

        {/* A promise the screen can keep now that History lists every run with its own document. */}
        <Body>All prior benefit verifications can still be downloaded under History.</Body>

        <Details>
          <Row>
            <Label>Payer Name</Label>
            <Value data-testid='rerun-payer-name'>{payerName || '—'}</Value>
          </Row>
          <Row>
            <Label>Member ID</Label>
            <Value data-testid='rerun-member-id'>{memberId || '—'}</Value>
          </Row>
        </Details>

        <Actions>
          <Button variant='ghost' onClick={onCancel} data-testid='rerun-cancel'>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isRerunning} loading={isRerunning} data-testid='rerun-confirm'>
            Re-Run Benefit Verification
          </Button>
        </Actions>
      </Content>
    </StandardModal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.black10};

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled(BodySmall)`
  width: 40%;
  color: ${colors.black50};
`;

const Value = styled(Body)`
  color: ${colors.black100};
`;
